import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  CurrencyCode,
  DocumentType,
  PaymentMethod,
  UserRole,
  convertUsdMinorToVesMinor,
  formatMoney,
  toMinorUnits,
  type Customer,
} from '@lombok/shared';
import { ApiError, putToPresignedUrl } from '@lombok/api-client';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@lombok/ui';
import { PageHeader } from '@/components/PageHeader';
import { BackLink } from '@/components/DetailRow';
import { FORM_COLUMN } from '@/components/layout';
import { Field, FormError, SelectField, TextField, TextareaField } from '@/components/form';
import { useSessionProfile } from '@/auth/useSessionProfile';
import { api } from '@/lib/api';
import { parseDecimal } from '@/lib/decimal';
import { paymentLabel } from '@/lib/names';
import { useCreatePayment } from '@/data/hooks';
import { CustomerPicker } from '@/features/orders/CustomerPicker';
import {
  PAYMENT_METHOD_OPTIONS,
  PROOF_ACCEPT,
  isAllowedProofContentType,
} from './labels';

interface PaymentUploadPageProps {
  /** Group root for the back link + success "Ver pago" link, e.g. "/app/customer/pagos". */
  basePath: string;
}

/** Whose account a self-reported payment belongs to (derived from the session). */
type AccountTarget =
  | { kind: 'customer'; accountId: string; label?: string }
  | { kind: 'distributor'; accountId: string; label?: string }
  | { kind: 'pick' };

/** A fresh idempotency key per attempt (retry-safe; rotated after success). */
function newIdempotencyKey(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return `pay-${c.randomUUID()}`;
  return `pay-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Subir comprobante de pago. The user reports an offline/manual payment and
 * attaches a proof image/PDF. Flow: pick the account (own for customer/
 * distributor users; chosen for sales/admin) → fill method/amount/currency/
 * (rate if VES)/reference/(optional order) → presign → PUT the file straight to
 * S3 → POST /payments with the returned `proofS3Key`. The payment is created
 * `pending`; finance approves it later. Money is in minor units via shared
 * helpers, displayed USD + VES.
 */
export function PaymentUploadPage({ basePath }: PaymentUploadPageProps) {
  const navigate = useNavigate();
  const { profile } = useSessionProfile();
  const mutation = useCreatePayment();

  // Resolve the target account from the role. Customer/distributor users post to
  // their own account; sales/admin/finance pick a customer.
  const target = useMemo<AccountTarget>(() => {
    const role = profile?.role;
    if (role === UserRole.CUSTOMER_USER && profile?.customerId) {
      return { kind: 'customer', accountId: profile.customerId };
    }
    if (
      (role === UserRole.DISTRIBUTOR_ADMIN || role === UserRole.DISTRIBUTOR_USER) &&
      profile?.distributorId
    ) {
      return { kind: 'distributor', accountId: profile.distributorId };
    }
    return { kind: 'pick' };
  }, [profile]);

  const [pickedCustomer, setPickedCustomer] = useState<Customer | null>(null);
  const [method, setMethod] = useState<string>(PaymentMethod.PAGO_MOVIL);
  const [currency, setCurrency] = useState<string>(CurrencyCode.USD);
  const [amountText, setAmountText] = useState('');
  const [rateText, setRateText] = useState('');
  const [reference, setReference] = useState('');
  const [relatedOrderId, setRelatedOrderId] = useState('');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [created, setCreated] = useState<{ paymentId: string; paymentNumber?: string } | null>(null);
  const [idemKey, setIdemKey] = useState(newIdempotencyKey);

  // The effective account this payment posts to.
  const account =
    target.kind === 'pick'
      ? pickedCustomer
        ? { kind: 'customer' as const, accountId: pickedCustomer.customerId }
        : null
      : { kind: target.kind, accountId: target.accountId };

  const isVes = currency === CurrencyCode.VES;
  const amount = parseDecimal(amountText);
  const amountValid = Number.isFinite(amount) && amount > 0;
  const rate = parseDecimal(rateText);
  const rateValid = Number.isFinite(rate) && rate > 0;

  // Live USD equivalent for a VES amount (display only; the backend recomputes).
  const usdEquivMinor =
    isVes && amountValid && rateValid
      ? Math.round(toMinorUnits(amount, CurrencyCode.VES) / rate)
      : amountValid && !isVes
        ? toMinorUnits(amount, CurrencyCode.USD)
        : 0;
  const vesEquivMinor =
    !isVes && amountValid && rateValid
      ? convertUsdMinorToVesMinor(toMinorUnits(amount, CurrencyCode.USD), rate)
      : isVes && amountValid
        ? toMinorUnits(amount, CurrencyCode.VES)
        : 0;

  const canSubmit =
    Boolean(account) &&
    amountValid &&
    (!isVes || rateValid) &&
    Boolean(file) &&
    !uploading &&
    !mutation.isPending;

  function onPickFile(next: File | null) {
    setFieldError(null);
    if (next && !isAllowedProofContentType(next.type)) {
      setFile(null);
      setFieldError('Formato no permitido. Sube una imagen (PNG, JPG, WEBP) o un PDF.');
      return;
    }
    setFile(next);
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFieldError(null);

    if (!account) {
      setFieldError('Selecciona la cuenta del pago.');
      return;
    }
    if (!file) {
      setFieldError('Adjunta el comprobante de pago.');
      return;
    }
    if (!amountValid) {
      setFieldError('Ingresa un monto válido.');
      return;
    }
    if (isVes && !rateValid) {
      setFieldError('Ingresa la tasa de cambio (VES por USD) para un pago en bolívares.');
      return;
    }

    const contentType = file.type || 'application/octet-stream';

    try {
      setUploading(true);
      // 1) Ask the backend for a short-lived presigned PUT URL (no secrets in the browser).
      const presigned = await api.presignUpload({
        accountId: account.accountId,
        documentType: DocumentType.PAYMENT_PROOF,
        fileName: file.name,
        contentType,
      });
      // 2) Upload the file straight to S3 (raw PUT, not through the envelope
      // client). Send the SAME content type we signed with, or S3 rejects it.
      await putToPresignedUrl(presigned, file, { contentType });
      setUploading(false);

      // 3) Register the pending payment, linking the uploaded proof by its S3 key.
      mutation.mutate(
        {
          accountId: account.accountId,
          accountType: account.kind,
          amount,
          currency: currency as CurrencyCode,
          exchangeRate: isVes ? rate : rateValid ? rate : undefined,
          method: method as PaymentMethod,
          referenceNumber: reference.trim() || undefined,
          relatedOrderId: relatedOrderId.trim() || undefined,
          notes: notes.trim() || undefined,
          proofS3Key: presigned.s3Key,
          idempotencyKey: idemKey,
        },
        { onSuccess: (payment) => setCreated({ paymentId: payment.paymentId, paymentNumber: payment.paymentNumber }) },
      );
    } catch (err) {
      setUploading(false);
      setFieldError(
        err instanceof ApiError
          ? err.message
          : 'No se pudo subir el comprobante. Intenta de nuevo.',
      );
    }
  }

  function resetForm() {
    setCreated(null);
    setPickedCustomer(null);
    setMethod(PaymentMethod.PAGO_MOVIL);
    setCurrency(CurrencyCode.USD);
    setAmountText('');
    setRateText('');
    setReference('');
    setRelatedOrderId('');
    setNotes('');
    setFile(null);
    setFieldError(null);
    setIdemKey(newIdempotencyKey());
  }

  // ---- Success panel ----
  if (created) {
    return (
      <div className={`flex flex-col gap-6 ${FORM_COLUMN}`}>
        <div className="flex flex-col gap-2">
          <BackLink to={basePath}>Pagos</BackLink>
          <PageHeader
            title="Comprobante enviado"
            description="Tu pago quedó registrado y está en revisión."
          />
        </div>
        <Card>
          <CardContent className="flex flex-col gap-4 pt-5">
            <div className="rounded-md border border-info-soft bg-info-soft/50 p-3">
              <p className="text-sm font-medium text-text-primary">
                Recibimos tu comprobante. Finanzas lo revisará y verás el resultado en tus
                notificaciones.
              </p>
            </div>
            <div>
              <p className="text-sm text-text-muted">Número de pago</p>
              <p className="font-mono text-sm font-medium text-text-primary">{paymentLabel(created)}</p>
            </div>
            <div className="flex flex-col-reverse gap-2 border-t border-border pt-4 sm:flex-row sm:justify-end">
              <Button variant="outline" onClick={resetForm}>
                Registrar otro pago
              </Button>
              <Link to={`${basePath}/${created.paymentId}`}>
                <Button className="w-full sm:w-auto">Ver pago</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const submitting = uploading || mutation.isPending;

  // ---- Form ----
  return (
    <div className={`flex flex-col gap-6 ${FORM_COLUMN}`}>
      <div className="flex flex-col gap-2">
        <BackLink to={basePath}>Pagos</BackLink>
        <PageHeader
          title="Subir comprobante de pago"
          description="Reporta un pago (Pago Móvil, Zelle, transferencia…) y adjunta el comprobante."
        />
      </div>

      <form onSubmit={onSubmit} className="flex flex-col gap-6" noValidate>
        {/* 1. Cuenta (only when the role must pick) */}
        {target.kind === 'pick' ? (
          <Card>
            <CardHeader>
              <CardTitle>1. Cuenta</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {pickedCustomer ? (
                <div className="flex flex-col gap-3 rounded-md border border-border p-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex flex-col gap-1">
                    <span className="font-medium text-text-primary">{pickedCustomer.name}</span>
                    <div className="flex flex-wrap gap-x-4 text-xs text-text-muted">
                      <span>{pickedCustomer.rif}</span>
                      <span>Saldo: {formatMoney(pickedCustomer.openBalanceMinorUsd)}</span>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setPickedCustomer(null)}
                  >
                    Cambiar
                  </Button>
                </div>
              ) : (
                <CustomerPicker selectedId="" onSelect={setPickedCustomer} />
              )}
            </CardContent>
          </Card>
        ) : null}

        {/* Payment details */}
        <Card>
          <CardHeader>
            <CardTitle>{target.kind === 'pick' ? '2. Datos del pago' : 'Datos del pago'}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-5 pt-0">
            <div className="grid gap-5 sm:grid-cols-2">
              <SelectField
                label="Método de pago"
                required
                options={PAYMENT_METHOD_OPTIONS}
                value={method}
                onChange={(e) => setMethod(e.target.value)}
              />
              <SelectField
                label="Moneda"
                required
                options={[
                  { value: CurrencyCode.USD, label: 'USD (dólares)' },
                  { value: CurrencyCode.VES, label: 'VES (bolívares)' },
                ]}
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
              />
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <TextField
                label={isVes ? 'Monto (VES)' : 'Monto (USD)'}
                inputMode="decimal"
                required
                placeholder={isVes ? 'Ej. 1500,00' : 'Ej. 120,00'}
                value={amountText}
                onChange={(e) => setAmountText(e.target.value)}
              />
              <TextField
                label="Tasa de cambio (VES por USD)"
                inputMode="decimal"
                required={isVes}
                placeholder="Ej. 40,00"
                hint={
                  isVes
                    ? 'Requerida para pagos en bolívares.'
                    : 'Opcional. Se registra junto al pago.'
                }
                value={rateText}
                onChange={(e) => setRateText(e.target.value)}
              />
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <TextField
                label="Número de referencia"
                placeholder="Ref. de la transacción (opcional)"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
              />
              <TextField
                label="Pedido relacionado"
                placeholder="ID del pedido (opcional)"
                value={relatedOrderId}
                onChange={(e) => setRelatedOrderId(e.target.value)}
              />
            </div>

            {/* Live equivalents (display only). */}
            <div className="flex flex-col gap-2 rounded-md bg-surface-muted p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-text-muted">Equivalente (USD)</span>
                <span className="text-lg font-semibold tracking-tight text-text-primary">
                  {usdEquivMinor > 0 ? formatMoney(usdEquivMinor) : '—'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-text-muted">Equivalente (VES)</span>
                <span className="text-base font-medium text-text-primary">
                  {vesEquivMinor > 0 ? formatMoney(vesEquivMinor, CurrencyCode.VES) : '—'}
                </span>
              </div>
              {isVes && !rateValid ? (
                <p className="text-xs text-text-muted">
                  Ingresa la tasa para calcular el equivalente en dólares.
                </p>
              ) : null}
            </div>

            <ProofFileField file={file} onPick={onPickFile} disabled={submitting} />

            <TextareaField
              label="Notas"
              placeholder="Información adicional (opcional)."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />

            <FormError error={mutation.error} />
            {fieldError ? (
              <p className="text-sm text-danger" role="alert">
                {fieldError}
              </p>
            ) : null}

            <div className="flex flex-col-reverse gap-2 border-t border-border pt-4 sm:flex-row sm:justify-end">
              <Button type="button" variant="outline" onClick={() => navigate(basePath)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={!canSubmit}>
                {uploading
                  ? 'Subiendo comprobante…'
                  : mutation.isPending
                    ? 'Registrando…'
                    : 'Enviar comprobante'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}

/** File picker for the proof, with the selected file name + a clear action. */
function ProofFileField({
  file,
  onPick,
  disabled,
}: {
  file: File | null;
  onPick: (file: File | null) => void;
  disabled?: boolean;
}) {
  return (
    <Field
      label="Comprobante de pago"
      required
      hint="Imagen (PNG, JPG, WEBP) o PDF del comprobante."
    >
      {file ? (
        <div className="flex items-center justify-between gap-3 rounded-md border border-border-strong bg-surface p-3">
          <div className="flex min-w-0 flex-col">
            <span className="truncate text-sm font-medium text-text-primary">{file.name}</span>
            <span className="text-xs text-text-muted">{formatBytes(file.size)}</span>
          </div>
          <Button type="button" variant="ghost" size="sm" onClick={() => onPick(null)} disabled={disabled}>
            Quitar
          </Button>
        </div>
      ) : (
        <label
          className={`flex min-h-11 cursor-pointer items-center justify-center rounded-md border border-dashed border-border-strong bg-surface px-3 py-4 text-sm text-text-muted transition-colors hover:border-brand-accent hover:text-text-primary ${
            disabled ? 'pointer-events-none opacity-60' : ''
          }`}
        >
          <span>Seleccionar archivo…</span>
          <input
            type="file"
            accept={PROOF_ACCEPT}
            className="sr-only"
            disabled={disabled}
            onChange={(e) => onPick(e.target.files?.[0] ?? null)}
          />
        </label>
      )}
    </Field>
  );
}

/** Compact human file size (KB/MB) for the picked proof. */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(0)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}
